<script>
	import { onMount } from "svelte";
	import { navigate } from "svelte-routing";
	import { Button, Col, Row } from "@sveltestrap/sveltestrap";
	import CustomTableStatus from "../../../components/CustomTable/CustomTableStatus.svelte";
	import Loader from "../../../components/Loader/Loader.svelte";
	import WorkOrderIssuesTable from "./WorkOrderIssuesTable.svelte";
	import WorkOrderJobsTable from "./WorkOrderJobsTable.svelte";
	import EditWorkshopModal from "./EditWorkshopModal.svelte";
	import MoveToBacklogModal from "./MoveToBacklogModal.svelte";
	import MoveToAnotherWorkOrderModal from "./MoveToAnotherWorkOrderModal.svelte";
	import IssueDetailSidePanel from "../Manage/VehicleIssues/IssueDetailSidePanel.svelte";
	import JobDetailSidePanel from "./JobDetailSidePanel.svelte";
	import ChangeVehicleStatusModal from "./ChangeVehicleStatusModal.svelte";
	import MarkAsCompletedModal from "./MarkAsCompletedModal.svelte";
	import WorkshopNextStepModal from "./WorkshopNextStepModal.svelte";
	import WorkshopNotFixedReasonsModal from "./WorkshopNotFixedReasonsModal.svelte";
	import CancelWorkOrderModal from "./CancelWorkOrderModal.svelte";
	import CancelWorkOrderBlockedModal from "./CancelWorkOrderBlockedModal.svelte";
	import { convert } from "src/services/helpers";
	import {
		withoutWorkshopOnlyTransitions,
		workshopVisibleWorkOrderStatus,
		vehicleStatusLabel,
	} from "src/utils/misc";
	import { issueStatusOptions, jobStatusOptions } from "src/utils/options";
	import {
		toastMessage,
		cachedTaskLookups,
		cachedCancelReasons,
		userRole,
	} from "src/store/store";
	import {
		fetchTaskLookups,
		fetchCancelReasons,
	} from "src/services/lookupCache";
	import {
		createWorkshopSearch,
		loadWorkshopCatalog,
	} from "./workshopActions";
	import { isWshopLoginBypassEnabled } from "src/config/flag-config";
	import { usesWorkshopView } from "src/config/permissions";
	import {
		changeVehicleStatus as changeVehicleStatusApi,
		getVehicleDetails,
	} from "src/services/vehicle";
	import {
		getVehicleIssueById,
		deleteWorkOrderIssue,
		moveIssueToExistingWorkOrder,
		moveIssueToNewWorkOrder,
		updateIssueStatus,
		updateJobStatus,
		updateWorkOrderDestination,
		updateWorkOrderStatus,
	} from "src/services/maintenanceRepair";
	import {
		statusChangeErrorMessage,
		markWorkOrderCompleted,
		completeWorkshopWorkOrder,
		completedToastMessage,
		cancelWorkOrder,
	} from "./workOrderStatusActions";
	import { isJobClosed } from "./jobDisplay";

	export let vehicleId;
	export let workOrder;
	export let isLoadingWorkOrder = false;
	export let onAddJob = () => {};
	// workOrder is owned by MaintenanceRepairTabs, and Tabs.svelte recreates
	// this component from that copy on every tab switch — so each local
	// update has to flow back up too, or switching tabs undoes it.
	export let onWorkOrderChange = (_updatedWorkOrder) => {};

	function setWorkOrder(updatedWorkOrder) {
		workOrder = updatedWorkOrder;
		onWorkOrderChange(updatedWorkOrder);
	}

	const TERMINAL_WORK_ORDER_STATUSES = new Set(["Completed", "Cancelled"]);

	$: isWshop = usesWorkshopView($userRole);

	// The QA-login bypass (doLoginAsWorkshop) fakes userRole without a real
	// auth token, so real backend calls here would 401 and force-logout.
	const isUsingWshopBypass =
		isWshopLoginBypassEnabled && !localStorage.getItem("token");

	let isEditingWorkshop = false;
	let workshopOption = null;
	let workshopSaveError = "";
	let isSavingWorkshop = false;
	let isLoadingWorkshops = false;
	let hasLoadedWorkshops = false;
	let workshopLoadError = "";
	let workshopSearchError = "";

	const workshopSaveErrorMessages = {
		29202: "This work order has left Triage, so its destination can no longer be changed.",
		29207: "Select a workshop before saving.",
		29211: "Non-workshop work orders don't have a workshop destination.",
		29403: "That workshop is no longer available. Refresh and pick another one.",
	};

	let workshopOptions = [];
	let workshopLoadId = 0;

	async function loadWorkshops() {
		const loadId = ++workshopLoadId;
		workshopLoadError = "";
		isLoadingWorkshops = true;
		const { options, error } = await loadWorkshopCatalog();
		if (loadId !== workshopLoadId) return;
		workshopOptions = options;
		workshopLoadError = error;
		hasLoadedWorkshops = !error;
		isLoadingWorkshops = false;
	}

	let workshopFilterText = "";
	$: if (!workshopFilterText) workshopSearchError = "";

	const workshopSearch = createWorkshopSearch({
		getFilterText: () => workshopFilterText,
		getFallbackOptions: () => workshopOptions,
		onError: (error) => (workshopSearchError = error),
	});

	// Only the code is persisted on a cancelled work order — this maps it
	// back to a readable label for the "Reason for cancelling" row below.
	$: cancelReasonLabelByCode = Object.fromEntries(
		($cachedCancelReasons ?? []).map((reason) => [
			reason.code,
			reason.label,
		])
	);

	onMount(() => {
		fetchCancelReasons().catch(() => undefined);
	});

	onMount(() => {
		if (!isUsingWshopBypass) {
			fetchTaskLookups();
		}
	});

	let isMovingToBacklogModalOpen = false;
	let issueToMoveToBacklog = null;
	let isMovingToBacklog = false;
	let moveToBacklogError = "";

	let isMovingToAnotherWorkOrderModalOpen = false;
	let issueToMoveToAnotherWorkOrder = null;

	let isIssueDetailPanelOpen = false;
	let selectedIssue = null;

	async function openIssueDetail({ detail: issue }) {
		const res = await getVehicleIssueById(issue.id, vehicleId);
		selectedIssue = res.data || issue;
		isIssueDetailPanelOpen = true;
	}

	function handleIssueFlagToggled({ detail: { issueId, handoverFlagged } }) {
		setWorkOrder({
			...workOrder,
			issues: (workOrder.issues || []).map((i) =>
				i.id === issueId ? { ...i, handoverFlagged } : i
			),
		});
		if (selectedIssue?.id === issueId) {
			selectedIssue = { ...selectedIssue, handoverFlagged };
		}
	}

	let isJobDetailPanelOpen = false;
	let selectedJob = null;

	// Jobs aren't an M&R resource (see updateJobStatus above) — the row
	// already carries everything the panel shows, so there's no fetch here.
	function openJobDetail({ detail: job }) {
		selectedJob = job;
		isJobDetailPanelOpen = true;
	}

	let isVehicleStatusModalOpen = false;
	let vehicleStatus = "";

	$: vehicleStatusLookups = $cachedTaskLookups?.vehicleStatuses || [];

	// The "Change vehicle status" button only flip-flops between Active and
	// Inactive - In Workshop — there's no UI for picking any other status, so
	// any other current status (e.g. Decommissioned, Scrapped) gets no target.
	const VEHICLE_STATUS_TOGGLE = {
		active: "inactive - in workshop",
		"inactive - in workshop": "active",
	};

	$: targetVehicleStatusLabel =
		VEHICLE_STATUS_TOGGLE[vehicleStatus?.toLowerCase()];
	$: targetVehicleStatusOption = targetVehicleStatusLabel
		? vehicleStatusLookups.find(
				(option) =>
					vehicleStatusLabel(option).toLowerCase() ===
					targetVehicleStatusLabel
			)
		: undefined;
	// Attaching an issue directly isn't built — send the user to the Issues
	// tab with "Add issues to work order" pre-entered and targeted here.
	function goToAddIssueToWorkOrder() {
		navigate(
			`/maintenance-repair/${vehicleId}/issues?addToWorkOrder=${workOrder.id}`
		);
	}

	// Unlike goToAddIssueToWorkOrder above, this doesn't navigate — AddJob is
	// swapped in by the parent (MaintenanceRepairTabs) via this callback, so
	// the tab bar/breadcrumb stay mounted instead of relying on a URL change
	// that wouldn't remount this component anyway.
	function goToAddJob() {
		onAddJob();
	}

	function backToWorkOrders() {
		// Workshop users don't have access to the vehicle work orders list —
		// their only "back" is the workshop landing page itself.
		navigate(
			isWshop
				? "/maintenance-repair"
				: `/maintenance-repair/${vehicleId}/work-orders`
		);
	}

	let isSelectingIssuesForCompletion = false;
	let selectedIssueIdsForCompletion = [];

	function openMarkAsCompletedModal() {
		if (isWshop) {
			isSelectingIssuesForCompletion = true;
			selectedIssueIdsForCompletion = [];
			return;
		}
		isMarkAsCompletedModalOpen = true;
	}

	function cancelSelectingIssuesForCompletion() {
		isSelectingIssuesForCompletion = false;
		selectedIssueIdsForCompletion = [];
	}

	function confirmIssueSelectionForCompletion() {
		isSelectingIssuesForCompletion = false;
		const allFixed = (workOrder.issues || []).every((issue) =>
			selectedIssueIdsForCompletion.includes(issue.id)
		);
		if (allFixed) {
			isWorkshopNextStepModalOpen = true;
		} else {
			isNotFixedReasonsModalOpen = true;
		}
	}

	async function changeVehicleStatus() {
		if (isUsingWshopBypass) {
			toastMessage.set({
				type: "danger",
				message:
					"Changing vehicle status isn't available in the workshop QA bypass.",
			});
			return;
		}
		if (!vehicleStatusLookups.length) {
			await fetchTaskLookups();
		}
		const res = await getVehicleDetails(vehicleId);
		const currentOption = vehicleStatusLookups.find(
			(option) => option.id === res.data?.vehicleStatusId
		);
		vehicleStatus = currentOption ? vehicleStatusLabel(currentOption) : "";
		isVehicleStatusModalOpen = true;
	}

	function closeVehicleStatusModal() {
		isVehicleStatusModalOpen = false;
	}

	async function confirmChangeVehicleStatus() {
		if (!targetVehicleStatusOption) {
			isVehicleStatusModalOpen = false;
			return;
		}

		try {
			const res = await changeVehicleStatusApi(
				vehicleId,
				targetVehicleStatusOption.id
			);
			if (res.status === 200) {
				vehicleStatus = vehicleStatusLabel(targetVehicleStatusOption);
				toastMessage.set({
					type: "success",
					message:
						vehicleStatus.toLowerCase() === "active"
							? "Vehicle is active"
							: "Vehicle is inactive",
				});
			}
		} catch (error) {
		} finally {
			isVehicleStatusModalOpen = false;
		}
	}

	let isMarkAsCompletedModalOpen = false;
	let isWorkshopNextStepModalOpen = false;
	let isCancelWorkOrderModalOpen = false;
	let isCancelWorkOrderBlockedModalOpen = false;

	$: canCancelWorkOrder =
		(workOrder?.issues || []).length === 0 &&
		(workOrder?.jobs || []).every((job) => isJobClosed(job.status));

	function handleStatusChange({ detail: { newStatus } }) {
		if (newStatus === "Completed") {
			isMarkAsCompletedModalOpen = true;
			return;
		}
		if (newStatus === "Cancelled") {
			if (!canCancelWorkOrder) {
				isCancelWorkOrderBlockedModalOpen = true;
				return;
			}
			isCancelWorkOrderModalOpen = true;
			return;
		}

		updateWorkOrderStatus(workOrder.id, newStatus).then((res) => {
			if (res.error) {
				toastMessage.set({
					type: "danger",
					message:
						res.error === "WORKSHOP_CONFLICT"
							? `Only 1 work order can have "IN WORKSHOP" status at a time — ${res.conflictingWorkOrder.code} is already in the workshop.`
							: "Something went wrong. Please try again.",
				});
				return;
			}
			setWorkOrder({
				...workOrder,
				status: res.data.status,
				allowedTransitions: res.data.allowedTransitions,
			});
		});
	}

	function closeMarkAsCompletedModal() {
		isMarkAsCompletedModalOpen = false;
		selectedIssueIdsForCompletion = [];
	}

	async function confirmMarkAsCompleted({ detail: { setVehicleActive } }) {
		const res = await markWorkOrderCompleted(
			workOrder.id,
			workOrder.status,
			setVehicleActive,
			isWshop ? selectedIssueIdsForCompletion : undefined
		);
		isMarkAsCompletedModalOpen = false;
		selectedIssueIdsForCompletion = [];
		if (res.error) {
			toastMessage.set({
				type: "danger",
				message: statusChangeErrorMessage(res.error),
			});
			return;
		}

		// Returned issues left the work order for the backlog server-side —
		// drop them here too, or this now-completed page still shows them.
		const returnedIssueIds = new Set(res.data.returnedIssueIds || []);
		const completedIssueIds = new Set(res.data.completedIssueIds || []);
		setWorkOrder({
			...workOrder,
			status: res.data.status,
			allowedTransitions: res.data.allowedTransitions,
			lastUpdatedAt: res.data.completedAt,
			issues: (workOrder.issues || [])
				.filter((issue) => !returnedIssueIds.has(issue.id))
				.map((issue) =>
					completedIssueIds.has(issue.id)
						? {
								...issue,
								status: "Completed",
								allowedTransitions: [],
							}
						: issue
				),
		});
		toastMessage.set({
			type: "success",
			message: completedToastMessage(res),
		});
	}

	function closeWorkshopNextStepModal() {
		isWorkshopNextStepModalOpen = false;
		isSelectingIssuesForCompletion = false;
		selectedIssueIdsForCompletion = [];
	}

	let isNotFixedReasonsModalOpen = false;

	function confirmNextStep({ detail: { returnToHomeLot } }) {
		isWorkshopNextStepModalOpen = false;
		completeWorkshopPortion(returnToHomeLot, {});
	}

	// Reasons is only entered when the next-step question was skipped, so
	// "Back" returns to the checkbox selection.
	function backFromNotFixedReasons() {
		isNotFixedReasonsModalOpen = false;
		isSelectingIssuesForCompletion = true;
	}

	// An unresolved-but-explained issue is treated as ready for collection,
	// same as a fully-fixed one.
	function confirmNotFixedReasons({ detail: { notFixedReasonByIssueId } }) {
		isNotFixedReasonsModalOpen = false;
		completeWorkshopPortion(false, notFixedReasonByIssueId);
	}

	async function completeWorkshopPortion(
		returnToHomeLot,
		notFixedReasonByIssueId
	) {
		const issuesPayload = (workOrder.issues || []).map((issue) => {
			const isFixed = selectedIssueIdsForCompletion.includes(issue.id);
			return {
				issueId: issue.id,
				outcome: isFixed ? "Fixed" : "NotFixed",
				notFixedReason: isFixed
					? undefined
					: notFixedReasonByIssueId[issue.id],
			};
		});

		const res = await completeWorkshopWorkOrder(
			workOrder.id,
			returnToHomeLot,
			issuesPayload
		);
		selectedIssueIdsForCompletion = [];
		if (res.error) {
			toastMessage.set({
				type: "danger",
				message: statusChangeErrorMessage(res.error),
			});
			return;
		}

		const outcomeByIssueId = Object.fromEntries(
			res.data.issues.map((issue) => [issue.issueId, issue])
		);
		setWorkOrder({
			...workOrder,
			status: res.data.status,
			allowedTransitions: res.data.allowedTransitions,
			workshopCompletedAt: res.data.workshopCompletedAt,
			lastUpdatedAt: res.data.workshopCompletedAt,
			nextStep: res.data.nextStep,
			issues: (workOrder.issues || []).map((issue) => {
				const resolved = outcomeByIssueId[issue.id];
				return resolved
					? {
							...issue,
							outcome: resolved.outcome,
							unfixedReason: resolved.notFixedReason || null,
						}
					: issue;
			}),
		});
		toastMessage.set({
			type: "success",
			message: "Work order completed.",
		});
	}

	function closeCancelWorkOrderModal() {
		isCancelWorkOrderModalOpen = false;
	}

	async function confirmCancelWorkOrder({ detail: { reasonCode, notes } }) {
		const res = await cancelWorkOrder(
			workOrder.id,
			workOrder.status,
			reasonCode,
			notes
		);
		isCancelWorkOrderModalOpen = false;
		if (res.error) {
			toastMessage.set({
				type: "danger",
				message: statusChangeErrorMessage(res.error, "cancel"),
			});
			return;
		}

		setWorkOrder({
			...workOrder,
			status: res.data.status,
			allowedTransitions: res.data.allowedTransitions,
			lastUpdatedAt: res.data.cancelAt,
			cancelReasonCode: res.data.cancelReasonCode,
			cancelNotes: res.data.cancelNotes,
		});
		toastMessage.set({
			type: "success",
			message: "Work order cancelled.",
		});
	}

	function moveIssueToAnotherWorkOrder({ detail: issue }) {
		issueToMoveToAnotherWorkOrder = issue;
		isMovingToAnotherWorkOrderModalOpen = true;
	}

	function closeMoveToAnotherWorkOrderModal() {
		isMovingToAnotherWorkOrderModalOpen = false;
		issueToMoveToAnotherWorkOrder = null;
	}

	async function confirmMoveToAnotherWorkOrder({
		detail: { issue, mode, targetWorkOrderOption },
	}) {
		// Still mocked: moving needs DELETE then POST with addedVia "Regrouped".
		const res =
			mode === "existing"
				? await moveIssueToExistingWorkOrder(
						issue.id,
						targetWorkOrderOption.value
					)
				: await moveIssueToNewWorkOrder([issue.id], { vehicleId });

		isMovingToAnotherWorkOrderModalOpen = false;
		issueToMoveToAnotherWorkOrder = null;

		if (res.error) {
			toastMessage.set({
				type: "danger",
				message: "Something went wrong. Please try again.",
			});
			return;
		}

		const targetWorkOrderId =
			mode === "existing" ? res.data.workOrder.id : res.data.id;

		toastMessage.set({
			type: "success",
			message:
				mode === "create"
					? "Issue moved to a new work order."
					: "Issue moved to another work order.",
		});
		navigate(
			`/maintenance-repair/${vehicleId}/details/${targetWorkOrderId}`
		);
	}

	function openMoveToBacklogModal({ detail: issue }) {
		issueToMoveToBacklog = issue;
		moveToBacklogError = "";
		isMovingToBacklogModalOpen = true;
	}

	function closeMoveToBacklogModal() {
		if (isMovingToBacklog) return;
		isMovingToBacklogModalOpen = false;
		issueToMoveToBacklog = null;
	}

	async function confirmMoveIssueToBacklog() {
		const issue = issueToMoveToBacklog;
		if (!issue || isMovingToBacklog) return;

		isMovingToBacklog = true;
		moveToBacklogError = "";
		let res;
		try {
			res = await deleteWorkOrderIssue(workOrder.id, issue.id);
		} finally {
			isMovingToBacklog = false;
		}
		if (res.error) {
			// Stays open with the backend's message in the modal's banner.
			moveToBacklogError =
				res.error.message || "Something went wrong. Please try again.";
			return;
		}

		isMovingToBacklogModalOpen = false;
		issueToMoveToBacklog = null;
		setWorkOrder({
			...workOrder,
			issues: (workOrder.issues || []).filter((i) => i.id !== issue.id),
		});
		toastMessage.set({
			type: "success",
			message: "Issue moved to backlog.",
		});
	}

	function startEditWorkshop() {
		workshopOption = workOrder.destination?.workshopId
			? {
					label: workOrder.destination.workshopName,
					value: workOrder.destination.workshopId,
				}
			: null;
		workshopFilterText = "";
		workshopSaveError = "";
		workshopSearchError = "";
		workshopSearch.invalidate();
		isEditingWorkshop = true;
		if (!isUsingWshopBypass) loadWorkshops();
	}

	async function saveWorkshop() {
		if (isSavingWorkshop) return;
		if (!workshopOption) {
			workshopSaveError = workshopSaveErrorMessages[29207];
			return;
		}

		isSavingWorkshop = true;
		let res;
		try {
			res = await updateWorkOrderDestination(workOrder.id, {
				workshopId: workshopOption.value,
			});
		} finally {
			isSavingWorkshop = false;
		}
		if (res.error) {
			// Shown in the modal's error banner, backend message first.
			workshopSaveError =
				res.error.message ||
				workshopSaveErrorMessages[res.error.code] ||
				"Something went wrong. Please try again.";
			return;
		}

		setWorkOrder({ ...workOrder, destination: res.data.destination });
		isEditingWorkshop = false;
		toastMessage.set({
			type: "success",
			message: "Change saved.",
		});
	}

	function cancelEditWorkshop() {
		if (isSavingWorkshop) return;
		workshopSaveError = "";
		workshopSearch.invalidate();
		isEditingWorkshop = false;
	}

	$: hasWorkshopIssue = (workOrder?.issues || []).some((issue) =>
		["Bodywork", "Maintenance"].includes(issue.serviceCategory)
	);

	// The dropdown displays/selects labels (like the Issues tab and Vehicle
	// Work Orders list do); this converts a selected label back to the raw
	// value the mock API expects.
	const issueStatusValueByLabel = Object.fromEntries(
		issueStatusOptions.map((option) => [option.label, option.value])
	);

	async function handleIssueStatusSelect({ detail: { issue, statusLabel } }) {
		const newStatus = issueStatusValueByLabel[statusLabel] || statusLabel;
		const res = await updateIssueStatus(issue.id, newStatus);
		if (res.error) {
			toastMessage.set({
				type: "danger",
				message: "Something went wrong. Please try again.",
			});
			return;
		}

		setWorkOrder({
			...workOrder,
			issues: (workOrder.issues || []).map((i) =>
				i.id === issue.id
					? {
							...i,
							status: newStatus,
							allowedTransitions: res.data.allowedTransitions,
						}
					: i
			),
		});
	}

	const jobStatusValueByLabel = Object.fromEntries(
		jobStatusOptions.map((option) => [option.label, option.value])
	);

	async function handleJobStatusSelect({
		detail: { job, statusLabel, reasonCode, notes },
	}) {
		const newStatus = jobStatusValueByLabel[statusLabel] || statusLabel;
		const res = await updateJobStatus(job.jobId, newStatus, {
			reasonCode,
			notes,
		});
		if (res.error) {
			toastMessage.set({
				type: "danger",
				message: "Something went wrong. Please try again.",
			});
			return;
		}

		setWorkOrder({
			...workOrder,
			jobs: (workOrder.jobs || []).map((j) =>
				j.jobId === job.jobId
					? {
							...j,
							status: newStatus,
							allowedTransitions: res.data.allowedTransitions,
							cancelReasonCode: res.data.cancelReasonCode,
							cancelNotes: res.data.cancelNotes,
						}
					: j
			),
		});

		if (newStatus === "cancelled") {
			toastMessage.set({ type: "success", message: "Job cancelled" });
		}
	}
</script>

{#if isLoadingWorkOrder}
	<div class="d-flex justify-content-center mt-5">
		<Loader />
	</div>
{:else if workOrder}
	<div class="d-flex align-items-center mt-4" style="gap: 12px;">
		<h5 class="mb-0 fw-bold">{workOrder.code}</h5>
		<CustomTableStatus
			row={isWshop || TERMINAL_WORK_ORDER_STATUSES.has(workOrder.status)
				? {
						...workOrder,
						status: isWshop
							? workshopVisibleWorkOrderStatus(workOrder.status)
							: workOrder.status,
						allowedTransitions: [],
					}
				: {
						...workOrder,
						// "Completed" is a button below, not a dropdown option.
						allowedTransitions: withoutWorkshopOnlyTransitions(
							workOrder.allowedTransitions
						).filter((transition) => transition !== "Completed"),
					}}
			key="status"
			options="workOrder"
			on:statusChange={handleStatusChange}
		/>
		<div class="ms-auto d-flex" style="gap: 12px;">
			{#if isWshop}
				{#if !isSelectingIssuesForCompletion}
					<Button
						class="themeButton backButton"
						on:click={backToWorkOrders}
					>
						Back to vehicles
					</Button>
					{#if workOrder.status === "InWorkshop"}
						<Button
							class="themeButton backButton"
							on:click={openMarkAsCompletedModal}
						>
							Mark work order as complete
						</Button>
					{/if}
				{/if}
			{:else}
				{#if workOrder.status !== "Cancelled"}
					<Button
						class="themeButton backButton"
						on:click={changeVehicleStatus}
						>Change vehicle status</Button
					>
				{/if}
				<Button
					class="themeButton backButton"
					on:click={backToWorkOrders}
				>
					Back to work orders
				</Button>
				{#if (workOrder.allowedTransitions || []).includes("Completed")}
					<Button
						class="themeButton primary"
						on:click={openMarkAsCompletedModal}
					>
						Mark as completed
					</Button>
				{/if}
			{/if}
		</div>
	</div>

	<div class="my-5">
		<Row class="my-3">
			<Col xs={3}><b>Last updated</b></Col>
			<Col xs={3}><span>{convert(workOrder.lastUpdatedAt)}</span></Col>
		</Row>
		<Row class="my-3">
			<Col xs={3}><b>Date created</b></Col>
			<Col xs={3}><span>{convert(workOrder.createdAt)}</span></Col>
		</Row>
		<Row class="my-3">
			<Col xs={3} class="d-flex align-items-center" style="gap: 8px;">
				<b>Workshop</b>
				{#if !isWshop && hasWorkshopIssue}
					<img
						src="/assets/icon-alert-filled-red.svg"
						alt="Has workshop issue"
						title="This work order has a Bodywork or Maintenance issue that requires a workshop visit"
						width="24"
						height="24"
					/>
				{/if}
			</Col>
			<Col xs={3} class="d-flex align-items-center" style="gap: 6px;">
				<span>{workOrder.destination?.workshopName || "-"}</span>
				{#if !isWshop && workOrder.status === "Triage"}
					<button
						type="button"
						class="edit-workshop-icon-btn"
						aria-label="Edit workshop"
						on:click={startEditWorkshop}
					>
						<img
							src="/assets/icon-pen.svg"
							alt=""
							width="24"
							height="24"
						/>
					</button>
				{/if}
			</Col>
		</Row>
		<Row class="my-3">
			<Col xs={3}><b>Date completed by workshop</b></Col>
			<Col xs={3}>
				<span>
					{workOrder.workshopCompletedAt
						? convert(workOrder.workshopCompletedAt)
						: "-"}
				</span>
			</Col>
		</Row>
		<Row class="my-3">
			<Col xs={3}><b>Next step</b></Col>
			<Col xs={3}><span>{workOrder.nextStep || "-"}</span></Col>
		</Row>
		{#if workOrder.status === "Cancelled"}
			<Row class="my-3">
				<Col xs={3}><b>Reason for cancelling</b></Col>
				<Col xs={3}>
					<span>
						{cancelReasonLabelByCode[workOrder.cancelReasonCode] ||
							workOrder.cancelReasonCode ||
							"-"}
						{#if workOrder.cancelNotes}
							— {workOrder.cancelNotes}
						{/if}
					</span>
				</Col>
			</Row>
		{/if}
	</div>

	<hr class="divider" />

	<WorkOrderIssuesTable
		issues={workOrder.issues || []}
		readOnly={TERMINAL_WORK_ORDER_STATUSES.has(workOrder.status)}
		{isWshop}
		isSelectingForCompletion={isSelectingIssuesForCompletion}
		showCompletionOutcome={isWshop && workOrder.status !== "InWorkshop"}
		bind:selectedIssueIds={selectedIssueIdsForCompletion}
		on:openDetail={openIssueDetail}
		on:statusSelect={handleIssueStatusSelect}
		on:moveToAnotherWorkOrder={moveIssueToAnotherWorkOrder}
		on:moveToBacklog={openMoveToBacklogModal}
		on:addIssue={goToAddIssueToWorkOrder}
	/>

	{#if isSelectingIssuesForCompletion}
		<div class="d-flex justify-content-end mt-3" style="gap: 12px;">
			<Button
				class="themeButton backButton"
				on:click={cancelSelectingIssuesForCompletion}
			>
				Cancel
			</Button>
			<Button
				class="themeButton primary"
				on:click={confirmIssueSelectionForCompletion}
			>
				Confirm
			</Button>
		</div>
	{/if}

	<hr class="divider my-5" />

	<WorkOrderJobsTable
		jobs={workOrder.jobs || []}
		readOnly={TERMINAL_WORK_ORDER_STATUSES.has(workOrder.status)}
		on:statusSelect={handleJobStatusSelect}
		on:openDetail={openJobDetail}
		on:addJob={goToAddJob}
	/>

	<EditWorkshopModal
		isOpen={isEditingWorkshop}
		{workshopOptions}
		bind:workshopOption
		bind:filterText={workshopFilterText}
		{workshopSaveError}
		isSaving={isSavingWorkshop}
		{isLoadingWorkshops}
		{hasLoadedWorkshops}
		{workshopLoadError}
		{workshopSearchError}
		loadOptions={isUsingWshopBypass ? undefined : workshopSearch.search}
		onCancel={cancelEditWorkshop}
		onSave={saveWorkshop}
	/>

	<MoveToBacklogModal
		isOpen={isMovingToBacklogModalOpen}
		issue={issueToMoveToBacklog}
		isSubmitting={isMovingToBacklog}
		error={moveToBacklogError}
		on:close={closeMoveToBacklogModal}
		on:confirm={confirmMoveIssueToBacklog}
	/>

	<MoveToAnotherWorkOrderModal
		isOpen={isMovingToAnotherWorkOrderModalOpen}
		issue={issueToMoveToAnotherWorkOrder}
		{vehicleId}
		currentWorkOrderId={workOrder?.id}
		on:close={closeMoveToAnotherWorkOrderModal}
		on:confirm={confirmMoveToAnotherWorkOrder}
	/>

	<IssueDetailSidePanel
		isOpen={isIssueDetailPanelOpen}
		issue={selectedIssue}
		toggle={() => (isIssueDetailPanelOpen = !isIssueDetailPanelOpen)}
		{isWshop}
		{vehicleId}
		returnTo={`/maintenance-repair/${vehicleId}/details/${workOrder.id}`}
		on:flagToggled={handleIssueFlagToggled}
	/>

	<JobDetailSidePanel
		isOpen={isJobDetailPanelOpen}
		job={selectedJob}
		toggle={() => (isJobDetailPanelOpen = !isJobDetailPanelOpen)}
	/>

	<ChangeVehicleStatusModal
		isOpen={isVehicleStatusModalOpen}
		{vehicleStatus}
		targetStatusLabel={targetVehicleStatusOption
			? vehicleStatusLabel(targetVehicleStatusOption)
			: ""}
		on:close={closeVehicleStatusModal}
		on:confirm={confirmChangeVehicleStatus}
	/>

	<MarkAsCompletedModal
		isOpen={isMarkAsCompletedModalOpen}
		{workOrder}
		on:close={closeMarkAsCompletedModal}
		on:confirm={confirmMarkAsCompleted}
	/>

	<!-- Keyed on isOpen so a fresh instance is created each open, avoiding a
	     reactive reset guard racing against other derived state. -->
	{#key isWorkshopNextStepModalOpen}
		<WorkshopNextStepModal
			isOpen={isWorkshopNextStepModalOpen}
			on:close={closeWorkshopNextStepModal}
			on:confirm={confirmNextStep}
		/>
	{/key}

	{#key isNotFixedReasonsModalOpen}
		<WorkshopNotFixedReasonsModal
			isOpen={isNotFixedReasonsModalOpen}
			issues={(workOrder.issues || []).filter(
				(issue) => !selectedIssueIdsForCompletion.includes(issue.id)
			)}
			on:back={backFromNotFixedReasons}
			on:confirm={confirmNotFixedReasons}
		/>
	{/key}

	<CancelWorkOrderModal
		isOpen={isCancelWorkOrderModalOpen}
		on:close={closeCancelWorkOrderModal}
		on:confirm={confirmCancelWorkOrder}
	/>

	<CancelWorkOrderBlockedModal
		isOpen={isCancelWorkOrderBlockedModalOpen}
		toggle={() =>
			(isCancelWorkOrderBlockedModalOpen =
				!isCancelWorkOrderBlockedModalOpen)}
	/>
{:else}
	<p class="text-center mt-5">Work order not found.</p>
{/if}

<style>
	:global(.divider) {
		border: 0.5px solid var(--fds-outline-variant);
		margin: 24px 0;
	}

	.edit-workshop-icon-btn {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		border: none;
		background: none;
		padding: 2px;
		cursor: pointer;
		border-radius: 4px;
		transition:
			opacity 0.15s ease,
			background-color 0.15s ease;
	}

	.edit-workshop-icon-btn:hover {
		opacity: 0.7;
		background-color: var(--fds-surface-container-high);
	}
</style>
